const navigationOrigin = 'https://local.invalid';
const planPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const planKeyPattern = /^[a-z][a-z0-9-]{1,59}$/;

/** Sign-in can authorize a desktop client before the account selects a plan. */
export function desktopHandoffReturnPath(returnPath: string | undefined, basePath = ''): string | null {
 if (!returnPath) return null;
 let target: URL;
 try {target = new URL(returnPath, navigationOrigin);} catch {return null;}
 if (target.origin !== navigationOrigin || ![`${basePath}/desktop/authorize`, `${basePath}/desktop/browser`].includes(target.pathname) ||
   target.hash || [...target.searchParams.keys()].length !== 1 || !planPattern.test(target.searchParams.get('request') ?? '')) return null;
 return `${target.pathname}${target.search}`;
}

/** Billing plan keys come from billing_plans, never from a second route catalog. */
export function planSelectionPath(page:'billing'|'subscribe',selection:string,basePath=''):string {
 if(!planPattern.test(selection)&&!planKeyPattern.test(selection))throw new Error('Choose a valid subscription plan.');
 return `${basePath}/${page}/plans/${selection}`;
}

export function signupPlanSelection(returnPath:string|undefined,basePath=''):string|null {
 for(const target of navigationTargets(returnPath,basePath)){
  const relative=target.pathname.slice(basePath.length);
  const pathSelection=/^\/(?:billing|subscribe)\/plans\/([^/]+)$/.exec(relative)?.[1];
  const selection=pathSelection??target.searchParams.get('plan');
  if(selection&&(planPattern.test(selection)||planKeyPattern.test(selection)))return selection;
 }
 return null;
}

/** Read only local navigation state, including auth links wrapping a setup URL. */
function navigationTargets(returnPath: string | undefined, basePath: string): URL[] {
 const targets: URL[] = [];
 let next = returnPath;
 for (let depth = 0; next && depth < 4; depth++) {
  let target: URL;
  try {target = new URL(next, navigationOrigin);} catch {break;}
  if (target.origin !== navigationOrigin || !target.pathname.startsWith(`${basePath}/`)) break;
  targets.push(target);
  next = target.searchParams.get('next') ?? undefined;
 }
 return targets;
}

/** Keep a selected domain in the URL through sign-in and first-account setup. */
export function domainReturnPath(returnPath:string|undefined,basePath=''):string|null {
 for(const target of navigationTargets(returnPath,basePath)){
  if(target.pathname===`${basePath}/account/domains`){const domain=target.searchParams.get('domain');return domain&&/^[a-z0-9][a-z0-9.-]{1,251}[a-z0-9]$/i.test(domain)?`${target.pathname}?${new URLSearchParams({domain})}`:target.pathname;}
 }
 return null;
}

/** Navigation hints do not establish enrollment; the server validates the offer. */
export function signupPlanId(returnPath:string|undefined,basePath=''):string|null {
 for(const target of navigationTargets(returnPath,basePath)) {
  const planId=target.searchParams.get('plan');
  if(planId&&planPattern.test(planId))return planId;
 }
 return null;
}

/** A registered account selects its offer before workspace setup. */
export function signupPlanPath(returnPath:string|undefined,basePath=''):string {
 const selected=signupPlanSelection(returnPath,basePath);
 const query=new URLSearchParams({new_signup:'1'});
 const domain=domainReturnPath(returnPath,basePath);if(domain)query.set('next',domain);
 return `${selected?planSelectionPath('subscribe',selected,basePath):`${basePath}/subscribe`}?${query}`;
}

function selectedPlanPath(page:'setup'|'billing',planId:string,returnPath:string|undefined,basePath:string):string {
 if(!planPattern.test(planId))throw new Error('Choose a valid subscription plan before account setup.');
 const query=new URLSearchParams({plan:planId});
 const domain=domainReturnPath(returnPath,basePath);if(domain)query.set('next',domain);
 return `${basePath}/${page}?${query}`;
}

/** A signed-in account pays for its selected subscription before preparation. */
export function signupCheckoutPath(planId:string,returnPath:string|undefined,basePath=''):string {
 const domain=domainReturnPath(returnPath,basePath);
 return `${planSelectionPath('billing',planId,basePath)}${domain?`?${new URLSearchParams({next:domain})}`:''}`;
}

/** Email and Google registration share the same unpaid-account continuation. */
export function signupContinuationPath(planId:string|null,returnPath:string|undefined,basePath=''):string {
 const desktop = desktopHandoffReturnPath(returnPath, basePath);
 if (desktop) return desktop;
 return planId ? signupCheckoutPath(planId,returnPath,basePath) : signupPlanPath(returnPath,basePath);
}

/** Resume preparation after the server admits the workspace. */
export function signupSetupPath(planId:string,returnPath:string|undefined,basePath=''):string {
 return selectedPlanPath('setup',planId,returnPath,basePath);
}

/** A sign-in through the signup form does not make an existing account new. */
export function existingAccountPath(returnPath: string, basePath = ''): string {
 const target = new URL(returnPath, 'https://local.invalid');
 const onboarding = ((target.pathname === `${basePath}/subscribe` || target.pathname.startsWith(`${basePath}/subscribe/plans/`)) && target.searchParams.get('new_signup') === '1') ||
   target.pathname === `${basePath}/setup` ||
   (target.pathname === `${basePath}/billing` && target.searchParams.get('activate') === '1');
 if (!onboarding) return returnPath;
 const domain=domainReturnPath(returnPath,basePath);if(domain)return domain;
 const selected = signupPlanSelection(returnPath,basePath);
 // Preserve a deliberate plan choice for billing review, without activating or
 // replacing an existing subscriber's plan as a side effect of authentication.
 return selected ? signupCheckoutPath(selected,returnPath,basePath) : `${basePath}/workspace`;
}
